import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import BottomNavigation from '../components/reusable/BottomNav';
import BackButton from '../components/reusable/buttons/BackButton';
import Header from '../components/reusable/Header';
import MessageListCard from '../components/reusable/MessageListCard';
import Spinner from '../components/reusable/spinner/Spinner';
import NewMessageIcon from '../components/svgIcons/NewMessageIcon';
import { dispatch } from '../redux/store';

export const MESSAGE_REFRESH_INTERVAL = 15000; // 15 seconds

const MessageList = () => {
    const containerRef = useRef(null);
    const userInfo = useSelector(state => state.user.data);
    const chats = useSelector(state => state.chat.chats);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);

    const loadingUser = useSelector(
        ({ loading }) => loading.effects.user.fetchUserDetails
    );
    const loadingChats = useSelector(
        ({ loading }) => loading.effects.chat.getChats
    );
    const isLoading = (loadingUser || loadingChats) && !chats?.length;

    const fetchChats = async cursor => {
        const { nextCursor } = await dispatch.chat.getChats(cursor);
        return nextCursor || null;
    };

    useEffect(() => {
        const fetchData = async () => {
            const newCursor = await fetchChats();
            setNextCursor(newCursor || null);
        };

        if (!userInfo?.id) return;

        fetchData();

        const intervalId = setInterval(() => {
            fetchChats();
        }, MESSAGE_REFRESH_INTERVAL);

        return () => {
            clearInterval(intervalId);
        };
    }, [userInfo?.id]);

    const handleLoadMore = useCallback(async () => {
        if (!containerRef.current || !nextCursor || isLoadingMore) return;

        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

        if (isNearBottom) {
            setIsLoadingMore(true);
            const newCursor = await fetchChats(nextCursor);
            setNextCursor(newCursor || null);
            setIsLoadingMore(false);
        }
    }, [nextCursor, isLoadingMore]);

    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.addEventListener('scroll', handleLoadMore);
            return () =>
                containerRef.current?.removeEventListener(
                    'scroll',
                    handleLoadMore
                );
        }
    }, [handleLoadMore]);

    return (
        <div className='w-full h-screen page flex flex-col'>
            <Header>
                <div className='flex items-center justify-between w-full mt-10 gap-7'>
                    <BackButton from='' />
                    <div className='uppercase font-favela-bold text-[20px]'>
                        Messages
                    </div>
                    <Link to='/user/new-message' className='opacity-[0.6]'>
                        <NewMessageIcon />
                    </Link>
                </div>
            </Header>
            <div ref={containerRef} className='mt-2 flex-1 overflow-y-auto'>
                {chats?.length > 0 ? (
                    chats.map(chat => (
                        <div key={chat.chatroomId}>
                            <MessageListCard chat={chat} />
                        </div>
                    ))
                ) : (
                    <div className='w-full h-full py-16 text-2xl font-bold text-center text-gray-400 font-favela-bold'>
                        {isLoading ? <Spinner /> : 'No Messages'}
                    </div>
                )}
            </div>
            {isLoadingMore && (
                <div className='w-full flex items-center justify-center mt-2 mb-2'>
                    <Spinner />
                </div>
            )}
            <BottomNavigation />
        </div>
    );
};

export default MessageList;
